import React from "react";
import { motion } from "framer-motion";
import { ArrowUp, Heart, Mail, Phone, MapPin, ExternalLink, User } from "lucide-react";
import { FaFacebook, FaLinkedin, FaWhatsapp } from "react-icons/fa";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Enhanced animations
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
  };

  const fadeInLeft = {
    initial: { opacity: 0, x: -60 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
  };

  const fadeInRight = {
    initial: { opacity: 0, x: 60 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const floatingAnimation = {
    animate: {
      y: [0, -10, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Footer links organized by category
  const footerSections = [
    {
      title: "Solutions",
      links: [
        { name: "Mobile Apps Monetization", href: "/mobile-apps-monetization" },
        { name: "Advanced Ad Formats", href: "/advanced-ad-formats" },
        { name: "Header Bidding", href: "/header-bidding" },
        { name: "AMP Solution", href: "/amp-solution" },
        { name: "Video Content Creation", href: "/video-content-creation" },
        { name: "Video Monetization", href: "/video-monetization" }
      ]
    },
    {
      title: "Company",
      links: [
        { name: "Home", href: "/" },
        { name: "About Us", href: "/about" },
        {
          name: "Contact",
          href: "https://mail.google.com/mail/?view=cm&to=contact@monitize-media.com",
          target: "_blank"
        }
      ]
    },
    {
      title: "Legal",
      links: [
        { name: "Terms of Use", href: "/terms-of-use" },
        { name: "Privacy Policy", href: "/privacy-policy" },
        { name: "Cookie Policy", href: "/cookie-policy" }
      ]
    }
  ];

  const socialLinks = [
    { name: "LinkedIn", href: "https://www.linkedin.com/in/monitize-media/", icon: <FaLinkedin className="w-5 h-5" /> },
    { name: "Teams", href: "https://teams.live.com/l/invite/FEAxDdme_5KJYES-h8?v=g1", icon: <User className="w-5 h-5" /> },
    { name: "Facebook", href: "https://www.facebook.com/monitizemedia", icon: <FaFacebook className="w-5 h-5" /> },
   {
  name: "Whatsapp",
  href: "https://wa.me/905015396624",
  icon: <FaWhatsapp className="w-5 h-5" />,
  target: "_blank"
}

  ];

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-orange-500/10 to-purple-600/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-r from-blue-500/10 to-orange-600/10 rounded-full blur-3xl"></div>

        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(#ffffff33 1px, transparent 1px),
                             linear-gradient(90deg, #ffffff33 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="grid grid-cols-1 lg:grid-cols-4 gap-12 mb-16"
        >
          {/* Brand Section */}
          <motion.div variants={fadeInLeft} className="lg:col-span-1 space-y-6">
            <motion.div
              className="flex items-center space-x-4"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <motion.div
                className="w-14 h-14 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/25"
                whileHover={{ rotate: 5, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <span className="text-white font-bold text-2xl">M</span>
              </motion.div>
              <div>
                <h3 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Monitize Media
                </h3>
                <p className="text-orange-300 text-sm font-medium">Premium Ad Solutions</p>
              </div>
            </motion.div>

            <motion.p
              variants={fadeInUp}
              className="text-gray-400 leading-relaxed text-lg"
            >
              Empowering publishers with cutting-edge monetization solutions and premium advertising experiences.
            </motion.p>

            {/* Contact Info */}
            <motion.div variants={fadeInUp} className="space-y-4">
              {[
                { icon: Mail, text: "contact@monitize-media.com" },
                { icon: Phone, text: "+90 501 539 6624" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="flex items-center space-x-3 group cursor-pointer"
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center group-hover:bg-orange-500 transition-colors duration-300">
                    <item.icon className="w-4 h-4 text-orange-400 group-hover:text-white" />
                  </div>
                  <span className="text-gray-300 group-hover:text-white transition-colors duration-300">
                    {item.text}
                  </span>
                </motion.div>
              ))}
            </motion.div>

            {/* Social Links */}
            <motion.div variants={fadeInUp} className="flex space-x-3 pt-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center hover:bg-orange-500 transition-all duration-300 group relative"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <span className="text-lg">{social.icon}</span>
                  <div className="absolute -top-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap">
                    {social.name}
                  </div>
                </motion.a>
              ))}
            </motion.div>
          </motion.div>

          {/* Links Sections */}
          {footerSections.map((section, sectionIndex) => (
            <motion.div
              key={section.title}
              variants={fadeInUp}
              className="space-y-6"
            >
              <motion.h4
                className="text-xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent"
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                {section.title}
              </motion.h4>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <motion.li key={link.name}>
                    <motion.a
                      href={link.href}
                      className="flex items-center space-x-2 text-gray-400 hover:text-orange-400 transition-colors duration-300 group"
                      whileHover={{ x: 8 }}
                      transition={{ type: "spring", stiffness: 400 }}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                    >
                      <span>{link.name}</span>
                      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </motion.a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* Footer Bottom */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="pt-12 border-t border-gray-700/50 flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0"
        >
          <div className="text-gray-400 text-lg flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <motion.span
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-2"
            >
              <span>© {currentYear} Monitize Media</span>
            </motion.span>
            <span className="hidden sm:block">•</span>
            <motion.span
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-2"
            >
              <span>All rights reserved</span>
            </motion.span>
            <span className="hidden sm:block">•</span>
            <motion.span
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-2 text-orange-300"
            >
              <span>Made with</span>
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <Heart className="w-4 h-4 text-red-400 fill-current" />
              </motion.div>
              <span>for publishers</span>
            </motion.span>
          </div>

          {/* Scroll to Top Button */}
          <motion.button
            onClick={scrollToTop}
            className="group relative"
            whileHover="animate"
            whileTap={{ scale: 0.9 }}
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full opacity-0 group-hover:opacity-100 blur-md transition-opacity duration-300"
              variants={floatingAnimation}
            />
            <motion.div
              className="w-14 h-14 bg-gray-800 rounded-full flex items-center justify-center group-hover:bg-orange-500 transition-all duration-300 relative z-10 border border-gray-700 group-hover:border-orange-400"
              whileHover={{ scale: 1.1, rotate: 360 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <ArrowUp className="w-6 h-6 text-white" />
            </motion.div>
          </motion.button>
        </motion.div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-orange-400/30 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            animate={{
              y: [0, -100, 0],
              x: [0, 50, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 5 + Math.random() * 5,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>
    </footer>
  );
};

export default Footer;